import { useMemo } from "react";
import { useQuestionnaireContext } from "../context/QuestionnaireContext";
import { buildAttachmentsMap, buildFormAttachmentValues } from "../utils";

export const useQuestionnaireData = () => {
  const { questionnaireQuery, persistedFormData } = useQuestionnaireContext();

  const data = questionnaireQuery.data;
  if (!data) {
    throw new Error(
      "Questionnaire data is required to use useQuestionnaireData",
    );
  }

  const baseQuestionnaire = data.questionnaire;

  const regulations = data.regulations ?? {};
  const hasRegulations = Object.keys(regulations).length > 0;

  const questionnaireData = useMemo(
    () =>
      persistedFormData
        ? { ...baseQuestionnaire, ...persistedFormData }
        : baseQuestionnaire,
    [baseQuestionnaire, persistedFormData],
  );

  const attachments = useMemo(() => {
    const rawAttachments = data.attachments ?? {};
    return buildAttachmentsMap(rawAttachments);
  }, [data.attachments]);

  const formAttachmentValues = useMemo(
    () => buildFormAttachmentValues(attachments, questionnaireData),
    [attachments, questionnaireData],
  );

  const questionnaire = useMemo(
    () => ({
      ...questionnaireData,
      ...formAttachmentValues,
      regulations_accepted: hasRegulations
        ? questionnaireData.regulations_accepted
        : true,
      is_b2b_agreement: baseQuestionnaire.is_b2b_agreement,
    }),
    [
      questionnaireData,
      formAttachmentValues,
      hasRegulations,
      baseQuestionnaire.is_b2b_agreement,
    ],
  );

  const showRodoSection = !baseQuestionnaire.rodo_accepted;
  const showRegulationsSection =
    hasRegulations && !baseQuestionnaire.regulations_accepted;

  return {
    questionnaire,
    agreementStatus: baseQuestionnaire.agreement_status,
    canBeRejectedByContractor:
      questionnaireQuery.data.can_be_rejected_by_contractor,
    attachments,
    regulations,
    showRodoSection,
    showRegulationsSection,
  };
};
